const UPLOADER_READY_STATUS = 'ready';
const UPLOADER_UPLOAD_STATUS = 'upload';
const UPLOADER_ERROR_STATUS = 'error';
const UPLOADER_ABORTED_STATUS = 'aborted';
const UPLOADER_DONE_STATUS = 'done';

class Uploader {
  constructor() {
    this.fileNodes = [];
    this.fileSubs = [];
    this.validators = [];
  }

  ensureFileNode(file) {
    let node = this.fileNodes.find(fn => fn.file === file);
    if (!node) {
      node = { file, name: file.name, status: UPLOADER_READY_STATUS };
      node.isUploading = () => node.status === UPLOADER_UPLOAD_STATUS;
      node.isDone = () => node.status === UPLOADER_DONE_STATUS;
      node.abort = () => {
        if (node.req) {
          node.req.abort();
          node.req = undefined;
        }
        return Promise.resolve(node);
      };
      this.fileNodes.push(node);
    }
    return node;
  }

  nodeListChanged() {
    const ctx = {};
    this.validateFiles(ctx);
    const filesCopy = this.fileNodes.slice();
    this.notify('filesChanged', filesCopy, ctx);
  }

  nodeValidationErrorChanged(node, prevError) {
    this.notify('nodeValidationErrorChanged', node, prevError);
  }

  removeFiles(nodes) {
    const nodesToRemove = nodes === this.fileNodes ? this.fileNodes.slice() : nodes;
    return Promise.all(
      nodesToRemove.map((node) => {
        const index = this.fileNodes.indexOf(node);
        if (index !== undefined) {
          this.fileNodes.splice(index, 1);
          this.notify('fileRemoved', node);
          return node;
        }
        return null;
      })
      .filter(node => node)
      .map((node) => {
        return node.abort().catch(() => node).then(() => node);
      }),
    ).then((removedNodes) => {
      this.nodeListChanged();
      return removedNodes;
    });
  }

  addFiles(files) {
    const nodes = files.map(file => this.ensureFileNode(file));
    this.notify('filesAdded', nodes);
    this.nodeListChanged();
  }

  reset() {
    return this.removeFiles(this.getFileNodes());
  }

  resetDoneStates() {
    this.fileNodes = this.fileNodes.map((n) => {
      const node = n;
      if (node.isDone()) {
        node.status = UPLOADER_READY_STATUS;
        node.percent = 0;
        this.notify('fileStatusChanged', node);
      }
      return node;
    });
    return this.fileNodes;
  }

  getFileNodes() {
    return this.fileNodes;
  }

  getFileNodesToUpload() {
    return this.fileNodes.filter(node => !node.validationError && !node.isDone());
  }

  //
  // Validation
  //

  validateFiles(ctx = {}, notifyChange) {
    const newErrors = new Array(this.fileNodes.length);
    this.validators.forEach((v) => {
      const vCtx = v.startValidate && v.startValidate(ctx) || ctx;
      vCtx.nodes = this.fileNodes;
      this.fileNodes.forEach((node, i) => {
        newErrors[i] = newErrors[i] || v.validateFile(node, vCtx);
      });
      if (v.stopValidate) {
        v.stopValidate(vCtx);
      }
    });
    // check for changes
    let changed = false;
    this.fileNodes.forEach((node, i) => {
      if (node.validationError !== newErrors[i]) {
        const prevError = node.validationError;
        node.validationError = newErrors[i];
        this.nodeValidationErrorChanged(node, prevError);
        changed = true;
      }
    });
    if (changed) {
      ctx.validationChanged = true;
      if (notifyChange) {
        this.notify('filesChanged', this.fileNodes.slice(), ctx);
      }
    }
  }

  addValidator(v) {
    this.validators.push(v);
  }

  removeValidator(v) {
    const index = this.validators.indexOf(v);
    if (index >= 0) {
      this.validators.splice(index, 1);
    }
  }

  hasErrors() {
    return !!this.fileNodes.find(n => n.validationError || n.error);
  }

  //
  // Upload
  //
  upload(ctx = {}) {
    const nodesToUpload = this.fileNodes.filter(node => (node.status !== UPLOADER_UPLOAD_STATUS) && (node.status !== UPLOADER_DONE_STATUS));
    this.prepareUpload(nodesToUpload, ctx);
    this.notify('uploadStarted', nodesToUpload);
    const requestCtx = {
      uploadingCount: nodesToUpload.length,
    };
    // Not using Promise.all as it rejects as soon as one promise fails,
    // it does not wait for all promises to complete.
    return this.uploadStarted(ctx).then((uploadCtx) => {
      requestCtx.uploadCtx = uploadCtx;
      if (!nodesToUpload.length) {
        return { nodes: [], uploadCtx };
      }
      return new Promise((resolve, reject) => {
        nodesToUpload.forEach((node) => this.uploadFile(node, uploadCtx).catch(err => err).then(() => {
          requestCtx.uploadingCount -= 1;
          if (!requestCtx.uploadingCount) {
            const successNodes = nodesToUpload.filter(
                n => !n.error && (n.status === UPLOADER_DONE_STATUS),
            );
            const errorNodes = nodesToUpload.filter(n => n.error);
            if (errorNodes.length) {
              reject(errorNodes[0].error);
            } else {
              resolve(successNodes);
            }
          }
        }));
      });
    })
    .then((nodes) => {
      return this.uploadEnded(nodes, requestCtx.uploadCtx).then((result) => {
        this.notify('uploadEnded', result);
        return result;
      });
    })
    .catch((err) => {
      return this.uploadFailed(err, requestCtx.uploadCtx).then(() => {
        this.notify('uploadFailed', err);
        throw err;
      });
    });
  }

  prepareUpload(uploadNodes) {
    // Remove errors from nodes to upload and repo nodes
    uploadNodes.forEach((node) => {
      if (node.error) {
        node.error = null;
      }
    });
  }

  uploadStarted(ctx) {
    return Promise.resolve(ctx);
  }

  uploadEnded(nodes) {
    return Promise.resolve(nodes);
  }

  uploadFailed(err) {
    return Promise.resolve(err);
  }

  isUploading() {
    return this.fileNodes.find(node => node.isUploading());
  }

  uploadFile(node, ctx) {
    const changeStatus = (status, p) => {
      node.status = status;
      node.percent = p === undefined ? node.percent : p;
      this.notify('fileStatusChanged', node);
    };
    const getUploadRequestParams = () => {
      return this.getUploadRequestParams(node, ctx);
    };
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest();
      node.req = req;
      node.error = null;
      changeStatus(UPLOADER_UPLOAD_STATUS, 0);
      let reqClosed = false;
      req.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          changeStatus(UPLOADER_UPLOAD_STATUS, (event.loaded / event.total) * 100);
        }
      }, false);

      req.upload.addEventListener('load', () => {
        if (!node.error && !reqClosed) {
          changeStatus(UPLOADER_DONE_STATUS, 100);
        }
      });

      req.upload.addEventListener('error', () => {
        if (!reqClosed) {
          changeStatus(UPLOADER_ERROR_STATUS, req.statusText);
          node.error = new Error(req.statusText);
        }
      });

      req.upload.addEventListener('abort', () => {
        if (!reqClosed) {
          changeStatus(UPLOADER_ERROR_STATUS, 'Upload aborted');
          node.error = new Error('Upload aborted');
        }
      });

      req.upload.addEventListener('timeout', () => {
        if (!reqClosed) {
          changeStatus(UPLOADER_ERROR_STATUS, 'Timeout');
          node.error = new Error('Timeout');
        }
      });

      req.addEventListener('abort', () => {
        changeStatus(UPLOADER_ABORTED_STATUS, 'Upload aborted');
        node.error = new Error('Upload aborted');
      });

      req.addEventListener('error', () => {
        console.log('upload error ev', req.statusText);
        changeStatus(UPLOADER_ERROR_STATUS, req.statusText);
        node.error = new Error(req.statusText);
      });

      req.addEventListener('readystatechange', () => {
        if (req.readyState === 4) {
          // the transfer has completed and the server closed the connection.
          reqClosed = true;
          if (req.status >= 400) {
            node.error = new Error(req.statusText || `Server error code ${req.status}`);
            reject(node.error);
          } else if (node.error) {
            console.log('    node error', node.error);
            reject(node.error);
          } else {
            // Safari does not trigger events on XMLHttpRequest.upload
            if (!node.isDone()) {
              changeStatus(UPLOADER_DONE_STATUS, 100);
            }
            resolve(node);
          }
        }
      });

      const formData = new FormData();
      formData.append('file', node.file, node.file.name);

      const params = getUploadRequestParams();
      if (params.then) {
        params.then(({ method = 'POST', url }) => {
          req.open(method, url);
          req.send(formData);
        })
        .catch((err) => {
          changeStatus(UPLOADER_ERROR_STATUS, err);
          // throw err;
          reject(err);
        });
      } else {
        req.open(params.method || 'POST', params.url);
        req.send(formData);
      }
    })
    .then(() => {
      return this.nodeUploaded(node);
    })
    .catch((err) => {
      return this.nodeUploadFailed(node, err).finally(() => {
        node.error = err;
        this.notify('fileUploadFailed', node, err);
        throw err;
      });
    }).finally(() => {
      this.notify('fileUploadEnded', node, node.error);
      node.req = null;
    });
  }

  nodeUploadFailed(node, err) {
    return Promise.resolve(err);
  }

  nodeUploaded(node) {
    return node;
  }

  abortFile(node) {
    const uploader = this;
    return new Promise((resolve) => {
      const sub = {
        fileUploadEnded(n) {
          if (node === n) {
            uploader.unsubscribeUpload(sub);
            resolve(node);
          }
        },
      };
      uploader.subscribeUpload(sub);
      node.abort();
    });
  }

  abort() {
    const nodesToAbort = this.fileNodes.filter(node => node.isUploading());
    return Promise.all(nodesToAbort.map(node => this.abortFile(node)));
  }

  getUploadRequestParams() {}

  subscribeUpload(sub) {
    this.fileSubs.push(sub);
    return sub;
  }

  notify(event, ...params) {
    let index = this.fileSubs.length - 1;
    while (index >= 0) {
      const sub = this.fileSubs[index];
      if (sub[event]) {
        sub[event].apply(null, params);
      }
      index -= 1;
    }
  }

  unsubscribeUpload(sub) {
    const index = this.fileSubs.indexOf(sub);
    if (index >= 0) {
      this.fileSubs.splice(index, 1);
    }
  }
}

export {
  Uploader as default,
  UPLOADER_READY_STATUS,
  UPLOADER_UPLOAD_STATUS,
  UPLOADER_ERROR_STATUS,
  UPLOADER_ABORTED_STATUS,
  UPLOADER_DONE_STATUS,
};
